import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { clsx } from 'clsx'
import {
  PenTool, Search, Loader2, FileText, ListChecks, HelpCircle, Tag,
  ExternalLink, AlertCircle, Sparkles, History, Trash2, ChevronRight,
} from 'lucide-react'
import api from '../lib/api'
import { toast } from '../lib/toast'
import { isValidUrl } from '../lib/url'
import { useAuthStore } from '../store/authStore'
import WebsitePicker, { type PickerSite } from '../components/WebsitePicker'
import Dropdown from '../components/Dropdown'
import SubscriptionGate from '../components/SubscriptionGate'
import LoginRequired from '../components/LoginRequired'
import AiKeyBanner from '../components/AiKeyBanner'
import { isPaidPlan } from '../lib/reportPdf'
import { useSeoHead } from '../lib/seo'

const ENGINES = ['google.cz', 'google.com', 'google.sk', 'google.de', 'google.pl']

export default function ContentOptimizerPage() {
  const { t } = useTranslation()
  useSeoHead({
    title: t('content_opt.meta_title', { defaultValue: 'AI optimalizace obsahu — Content Optimizer | SEO Tools' }),
    description: t('content_opt.meta_desc', { defaultValue: 'Vylepšete obsah pomocí AI: návrhy klíčových slov, struktury a čitelnosti pro lepší pozice ve vyhledávačích i citace v AI vyhledávačích.' }),
    canonicalPath: '/content-optimizer',
  })
  const { isAuthenticated } = useAuthStore()
  // Funkce vyžaduje přihlášení (volá AI + ukládá historii). Anonym dostane výzvu k přihlášení
  // místo prázdné stránky a 401 dotazů.
  if (!isAuthenticated) return <LoginRequired feature={t('copt.title', { defaultValue: 'Content Optimizer' })} />
  return <ContentOptimizerInner />
}

function ContentOptimizerInner() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [keyword, setKeyword] = useState('')
  const [url, setUrl] = useState('')
  const [engine, setEngine] = useState('google.cz')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [gateOpen, setGateOpen] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [pickedDomain, setPickedDomain] = useState('')
  const [subpages, setSubpages] = useState<string[]>([])
  const [subLoading, setSubLoading] = useState(false)

  const queryClient = useQueryClient()
  const { data: websites } = useQuery({
    queryKey: ['websites'],
    queryFn: () => api.get('/websites').then((r) => r.data.websites),
  })

  // Historie optimalizací (uložené výsledky — stály AI tokeny, ať se neztratí)
  const { data: savedRuns } = useQuery({
    queryKey: ['content-optims'],
    queryFn: () => api.get('/content/optimizations').then((r) => r.data.items),
  })

  const loadSaved = async (id: number) => {
    try {
      const { data } = await api.get(`/content/optimizations/${id}`)
      const it = data.item
      setResult(it.result)
      setKeyword(it.keyword || '')
      setUrl(it.url || '')
      setEngine(it.engine || 'google.cz')
      setError('')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      toast.error(t('copt.load_error'))
    }
  }
  const deleteSaved = async (id: number, e: React.SyntheticEvent) => {
    e.stopPropagation()
    try {
      await api.delete(`/content/optimizations/${id}`)
      queryClient.invalidateQueries({ queryKey: ['content-optims'] })
      toast.success(t('copt.deleted'))
    } catch {
      toast.error(t('copt.delete_error'))
    }
  }
  const pickerSites = useMemo<PickerSite[]>(
    () => (websites ?? []).map((w: any) => ({ id: w.id, domain: w.domain, name: w.name, description: w.description, favicon_url: w.favicon_url })),
    [websites]
  )

  // Po výběru webu načte podstránky ze sitemapy, ať uživatel nemusí psát URL ručně
  const onPickDomain = async (d: string) => {
    setPickedDomain(d)
    const base = 'https://' + d.replace(/^https?:\/\//, '').replace(/\/$/, '')
    setUrl(base + '/')
    setSubLoading(true); setSubpages([])
    try {
      const { data } = await api.post('/tools/sitemap', { url: base })
      const list = data.entries || data.urls || []
      const urls: string[] = list.map((x: any) => (typeof x === 'string' ? x : x.loc)).filter(Boolean)
      setSubpages(urls.length ? Array.from(new Set(urls)).slice(0, 300) : [base + '/'])
    } catch {
      setSubpages([base + '/'])
    } finally {
      setSubLoading(false)
    }
  }

  const run = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!keyword.trim()) return
    if (url.trim() && !isValidUrl(url)) { toast.error(t('common.invalid_url')); return }
    if (!isPaidPlan(user?.plan)) { setGateOpen(true); return }
    setLoading(true); setError(''); setResult(null)
    try {
      const { data } = await api.post('/content/optimize', { keyword: keyword.trim(), url: url.trim() || undefined, engine })
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ['content-optims'] })
      toast.success(t('copt.saved'))
    } catch (err: any) {
      if (err?.response?.status === 403) setGateOpen(true)
      else setError(err?.response?.data?.message || t('copt.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
          <PenTool className="w-3.5 h-3.5" /> {t('copt.title')}
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('copt.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-2xl">{t('copt.subtitle')}</p>
      </motion.div>

      <AiKeyBanner />

      <form onSubmit={run} className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5 mb-8">
        {/* Výběr webu + podstránky — ať uživatel nemusí psát URL ručně */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
          <div className="md:col-span-6">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('copt.pick_web')}</label>
            <WebsitePicker sites={pickerSites} value={pickedDomain} onSelect={onPickDomain} placeholder={t('copt.pick_web_ph')} />
          </div>
          <div className="md:col-span-6">
            <label className="block text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1.5">
              {t('copt.subpage')} {subLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
            </label>
            <Dropdown
              value={url}
              onChange={(v) => setUrl(v)}
              options={subpages.map((u) => ({ value: u, label: u }))}
              placeholder={t('copt.pick_web_first')}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('copt.keyword')}</label>
            <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder={t('copt.keyword_ph')}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('copt.url')}</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t('copt.url_ph')}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('copt.engine')}</label>
            <Dropdown
              value={engine}
              onChange={(v) => setEngine(v)}
              options={ENGINES.map((en) => ({ value: en, label: en }))}
            />
          </div>
        </div>
        <button type="submit" disabled={loading || !keyword.trim()}
          className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? t('copt.analyzing') : t('copt.run')}
        </button>
        {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
        {loading && <p className="text-xs text-gray-400 mt-2">{t('copt.loading_hint')}</p>}
      </form>

      {/* Historie optimalizací — uložené výsledky k znovuotevření */}
      {savedRuns && savedRuns.length > 0 && (
        <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5 mb-8">
          <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white mb-3">
            <History className="w-4 h-4" /> {t('copt.recent')}
          </h2>
          <div className="divide-y divide-gray-50 dark:divide-white/5 -my-1">
            {savedRuns.map((r: any) => (
              <button key={r.id} onClick={() => loadSaved(r.id)}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/5 -mx-2 px-2 rounded-lg transition group">
                <span className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0">
                  <Tag className="w-4 h-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{r.keyword}</span>
                  <span className="block text-xs text-gray-400 truncate">
                    {r.url || '—'} · {r.engine} · {new Date(r.created_at).toLocaleString('cs-CZ')}
                  </span>
                </span>
                <span role="button" tabIndex={0} onClick={(e) => deleteSaved(r.id, e)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); deleteSaved(r.id, e) } }}
                  title={t('common.delete')}
                  className="text-gray-300 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-4 h-4" />
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-6">
          {/* Souhrnné metriky */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label={t('copt.recommended')} value={`${result.recommended_word_count}`} suffix={t('copt.words')} color="indigo" />
            <Stat label={t('copt.avg_competitors')} value={`${result.avg_word_count}`} suffix={t('copt.words')} color="cyan" />
            {result.target_word_count != null && (
              <Stat label={t('copt.your_page')} value={`${result.target_word_count}`} suffix={t('copt.words')}
                color={result.target_word_count >= result.avg_word_count ? 'green' : 'amber'} />
            )}
            <Stat label={t('copt.competitors')} value={`${result.competitors.length}`} suffix="" color="violet" />
          </div>

          {/* Chybí AI klíč → výzva přidat vlastní */}
          {result.brief?.need_key && (
            <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl p-4">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold text-amber-800 dark:text-amber-300">{t('copt.need_key_title', { defaultValue: 'Pro AI doporučení přidej svůj AI klíč' })}</p>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">{t('copt.need_key_desc', { defaultValue: 'Analýza konkurence funguje i bez klíče, ale AI obsahový brief vyžaduje vlastní API klíč (Gemini/Groq/OpenAI/Anthropic).' })}</p>
                <Link to="/profile" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-800 dark:text-amber-300 hover:underline mt-1.5">
                  {t('copt.add_key', { defaultValue: 'Přidat AI klíč v profilu' })} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* AI brief */}
          {result.brief && !result.brief.ai_unavailable && (result.brief.summary || result.brief.topics?.length > 0) && (
            <div className="bg-gradient-to-br from-primary-50/70 to-accent-400/5 dark:from-primary-500/10 dark:to-accent-500/5 rounded-2xl border border-primary-100 dark:border-primary-500/20 p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-primary-700 dark:text-primary-300 mb-3">
                <Sparkles className="w-4 h-4" /> {t('copt.ai_brief')}
              </h2>
              {result.brief.summary && <p className="text-sm text-gray-700 dark:text-gray-200 mb-4 leading-relaxed">{result.brief.summary}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.brief.topics?.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2"><ListChecks className="w-3.5 h-3.5" /> {t('copt.topics')}</p>
                    <ul className="space-y-1.5">
                      {result.brief.topics.map((tp: string, i: number) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300"><span className="text-primary-500">•</span>{tp}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.brief.questions?.length > 0 && (
                  <div>
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2"><HelpCircle className="w-3.5 h-3.5" /> {t('copt.questions')}</p>
                    <ul className="space-y-1.5">
                      {result.brief.questions.map((q: string, i: number) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300"><span className="text-accent-500">?</span>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chybějící termíny (když je zadaná URL) */}
          {result.target_url && result.missing_terms?.length > 0 && (
            <div className="bg-white dark:bg-white/5 rounded-2xl border border-amber-100 p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-amber-700 mb-3">
                <AlertCircle className="w-4 h-4" /> {t('copt.missing')} ({result.missing_terms.length})
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {result.missing_terms.map((tm: any) => (
                  <span key={tm.term} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {tm.term} <span className="text-amber-400">· {tm.in_competitors}×</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top termíny */}
          <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white mb-3"><Tag className="w-4 h-4 text-violet-500" /> {t('copt.terms')}</h2>
            <p className="text-xs text-gray-400 mb-3">{t('copt.terms_hint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {result.top_terms.map((tm: any) => (
                <span key={tm.term} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300">
                  {tm.term} <span className="text-gray-400">· {tm.in_competitors}/{result.competitors.length}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Konkurence */}
          <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white mb-3"><FileText className="w-4 h-4 text-gray-400" /> {t('copt.competitors')}</h2>
            <div className="space-y-2">
              {result.competitors.map((c: any, i: number) => (
                <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-50 dark:border-white/5 last:border-0">
                  <span className="w-6 text-center text-gray-400 font-mono text-xs">{c.position || i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 dark:text-gray-200 truncate">{c.title || c.url}</p>
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 truncate">
                      {c.url} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>
                  <span className="text-xs text-gray-500 shrink-0">{c.word_count} {t('copt.words')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <SubscriptionGate open={gateOpen} onClose={() => setGateOpen(false)} feature={t('copt.title')} />
    </div>
  )
}

function Stat({ label, value, suffix, color }: { label: string; value: string; suffix: string; color: string }) {
  const colors: Record<string, string> = {
    indigo: 'text-indigo-600 bg-indigo-50', cyan: 'text-cyan-600 bg-cyan-50',
    green: 'text-green-600 bg-green-50', amber: 'text-amber-600 bg-amber-50', violet: 'text-violet-600 bg-violet-50',
  }
  return (
    <div className="bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-4">
      <div className={clsx('inline-block px-2 py-0.5 rounded text-[10px] font-semibold mb-2', colors[color])}>{label}</div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value} <span className="text-sm font-normal text-gray-400">{suffix}</span></p>
    </div>
  )
}
